import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { camelCase_to_Sentence, pluralReadableTypeName, readableTypeName, toTitleCase } from '@iotv/core-services';
import { AlertIncidentType, Assembler, BranchingQueryDefinition, DeliveryType, DeviceClassName, edgeTypeMatrix, GatewayClassName, getItemTypeToGroupNestedAscendingQueryDef, getItemTypeToGroupNestedDescendingQueryDef, getSimpleBranchingQuery, IncidentTypeNames, NetworkApplicationServerClassName, NotificationSubscription, Ruleset, RuleToAlertIncidentMap, RuleTypes, Severity, SYSTEM, System, SystemClassName, } from '@iotv/datamodel';
import { matchEntityStates } from '@iotv/entity-state';
import { AdjacentType, getZombieInstanceFromPrimaryKey, IOTV_Notifiable_EventType } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes, getDeviceEnabledTypes } from '@iotv/payload-definition';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Alert, Autocomplete, Button, ButtonGroup, CircularProgress, Collapse, FormLabel, InputAdornment, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { deleteObject, getOne } from '../../../data/daoFunctions/daoFunctions';
import { isAdminRole } from '../../../helpers/access';
import { getUserToEntitiesInVisibleGroupsQueryDef, getUserToVisibleGroupsQueryDef } from '../../../helpers/queryHelpers';
import { ActionIcons } from '../../../Iconography';
import { AutosizedTextField } from '../../common/AutosizedTextField';
import { ConfirmLoadingButton } from '../../common/ConfirmLoadingButton';
import { SingleObjectAutoQuery } from '../../generic/ObjectAutoSearch';
import { displayMessage } from '../../messages/displayMessage';
import { getPartnerUsersForEntityOrGroup } from '../../Partner/partnerHelpers';
import { DeliveryOptions } from './DeliveryOption';
import { SubscriptionState } from './SubscriptionState';
const debug = process.env.REACT_APP_DEBUG && false;
export const AdminOnlyRuleTypes = [RuleTypes.SYSTEM, RuleTypes.NETWORK];
const validDeviceEnabledTypeIds = Object.keys(deviceEnabledTypes);
const ruleTypeToSelectableMap = {
    [RuleTypes.SYSTEM]: [SystemClassName],
    [RuleTypes.NETWORK]: [NetworkApplicationServerClassName, GatewayClassName, 'Group'],
    [RuleTypes.DEVICE]: [DeviceClassName, 'Group'],
    [RuleTypes.ENTITY]: [...validDeviceEnabledTypeIds, 'Group'],
};
export const SubscriptionLineItem = ({ contextCustomer, contextUser, userSelectedGroup, subscription, userFunctions, updateInParent, }) => {
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const referenceSubHash = [
        subscription?.incidentEntityOrGroupingSk,
        subscription?.incidentType,
        subscription?.subscriptionState.status,
        subscription?.subscriptionState?.reapplyAfterDateMs,
        subscription?.subscriptionState?.reapplyAfterEvent,
        subscription?.deliveryPreferences.map((item) => item.recipients.length).join(),
    ].join();
    const [incidentType, setIncidentType] = useState(subscription?.incidentType);
    const [ruleType, setRuleType] = useState(Object.values(RuleTypes)
        .filter((ruleType) => Object.values(RuleToAlertIncidentMap[ruleType]).includes(incidentType))
        .shift());
    const isAdmin = isAdminRole(userSelectedGroup);
    // Ruleset
    const [incidentSubtypeDefiningObject, setIncidentSubtypeDefiningObject] = useState(undefined);
    // System rule
    const [systemRule, setSystemRule] = useState(null);
    const [selectableEntityTypes, setSelectableEntityTypes] = useState([]);
    const [entityType, setEntityType] = useState(subscription?.incidentProducingTypeId !== 'Group' ? subscription?.incidentProducingTypeId : null);
    // choice between selecting entities or groups or global type, for a given `entityType`
    const [EntityGroupType, setEntityGroupType] = useState(subscription?.incidentEntityOrGroupingSk === `${subscription.incidentProducingTypeId}:*` ? 'Type' : entityType === null ? 'Group' : 'Entity');
    // filtered entityType, holds actual data
    const [objectType, setObjectType] = useState();
    const [triggeringEntityOrGroup, setTriggeringEntityOrGroup] = useState(null);
    const [systemRules, setSystemRules] = useState({});
    const [rulesets, setRulesets] = useState([]);
    const [severities, setSeverities] = useState(subscription?.filter.severities ?? []);
    const [eventTypes, setEventTypes] = useState(subscription?.filter.onActions ?? []);
    const [subscriptionState, setSubscriptionState] = useState(subscription?.subscriptionState ?? {});
    const [deliveryOptions, setDeliveryOptions] = useState(subscription?.deliveryPreferences ?? []);
    const [expanded, setExpanded] = useState(false);
    const [edited, setEdited] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [fetchingRulesets, setFetchingRulesets] = useState(false);
    /** Recipients for this notif */
    // Partner recipients - partners that can see the group or a parent of the group
    const [validPartnerRecipients, setValidPartnerRecipients] = useState([]);
    // TODO: Used to fetch partner names in the menu
    const [partnerTree, setPartnerTree] = useState(undefined);
    useEffect(() => {
        if (!triggeringEntityOrGroup) {
            setValidPartnerRecipients([]);
            return;
        }
        getPartnerUsersForEntityOrGroup(triggeringEntityOrGroup).then((partnerUsers_res) => {
            // TODO: add some indicator in the menu these are partner users. Probably best done in the autocomplete via
            const newValidPartnerRecipients = partnerUsers_res.partnerUsers;
            setValidPartnerRecipients(newValidPartnerRecipients);
            if (partnerUsers_res.tree) {
                setPartnerTree(partnerUsers_res.tree);
            }
            debug && console.log('SubscriptionLineItem', 'Valid Partner Recipients', newValidPartnerRecipients);
        });
    }, [triggeringEntityOrGroup?.sk, EntityGroupType]);
    // People
    // NOTE: possible optimisation: load people in SubscriptionsTab, because they don't change per subscription
    const [customersPeople, setCustomersPeople] = useState([]);
    useEffect(() => {
        // TODO: if people are ever put into groups, use getCustomerToTypeQuery('Person') instead
        ConsumerDao.getGraphQueryRequestV2({ query: getSimpleBranchingQuery('Customer', 'Person', AdjacentType.CHILD), contextObjects: [contextCustomer] }).then((people_res) => {
            if (people_res.err || !people_res.data) {
                console.error('Err retrieving people for Subscription:', people_res.err?.message ?? 'No data');
                return;
            }
            setCustomersPeople(people_res.data.getNodesOfType('Person'));
        });
    }, [contextCustomer.sk]);
    // save validation
    const [invalid, setInvalid] = useState(false);
    useEffect(() => {
        setInvalid(ruleType === undefined ||
            incidentType === undefined ||
            entityType === null ||
            (triggeringEntityOrGroup === null && EntityGroupType !== 'Type') ||
            (incidentType === AlertIncidentType.BUSINESS_RULE_VIOLATION && incidentSubtypeDefiningObject === undefined) ||
            (incidentType === AlertIncidentType.SYSTEM_RULE_VIOLATION && systemRule === null));
    }, [ruleType, incidentType, entityType, EntityGroupType, triggeringEntityOrGroup, incidentSubtypeDefiningObject, systemRule]);
    // filter entityType to appropriate actual objectType and supportedTypes
    useEffect(() => {
        if (entityType && EntityGroupType === 'Entity') {
            setObjectType(entityType);
        }
        if (entityType && EntityGroupType === 'Group') {
            setObjectType('Group');
        }
        if (entityType && EntityGroupType === 'Type') {
            setObjectType(entityType);
        }
    }, [EntityGroupType, entityType]);
    // if present, fetch the current triggering entity or group and update state
    useEffect(() => {
        if (subscription?.incidentEntityOrGroupingSk && subscription.incidentEntityOrGroupingSk !== subscription.incidentProducingTypeId + ':*')
            ConsumerDao.getExact({ pk: subscription?.incidentEntityOrGroupingSk, sk: subscription?.incidentEntityOrGroupingSk }).then((value) => {
                const { err, data } = value;
                if (err)
                    return;
                if (data) {
                    const item = data.Items?.[0];
                    if (item.type === 'Group')
                        setEntityType(item.supportedType);
                    setTriggeringEntityOrGroup(item);
                }
            });
    }, [subscription]);
    const currentSubHash = [
        triggeringEntityOrGroup?.sk,
        incidentType,
        subscriptionState.status,
        subscriptionState?.reapplyAfterDateMs,
        subscriptionState?.reapplyAfterEvent,
        deliveryOptions.map((item) => item.recipients.length).join(),
        deliveryOptions.map((item) => item.destinations).join(),
    ].join();
    const getObject = async (objectSk, setterFn, fallbackFn) => {
        if (!objectSk.includes(':'))
            fallbackFn(objectSk);
        const ob = Assembler.getZombieFromPrimaryKey(objectSk);
        if (ob) {
            getOne(ob).then(({ err, data }) => data && setterFn(data));
        }
        else {
            fallbackFn(objectSk);
        }
    };
    useEffect(() => {
        if (ruleType) {
            const selectableEntityTypes = ruleTypeToSelectableMap[ruleType];
            setSelectableEntityTypes(selectableEntityTypes.filter((entityType) => entityType !== 'Group'));
        }
        else {
            setSelectableEntityTypes([]);
        }
    }, [ruleType]);
    function getDeviceType(appliesToType) {
        const deviceEnabledTypes = getDeviceEnabledTypes();
        debug && console.log('DeviceEnabledTypes', deviceEnabledTypes);
        const deviceType = deviceEnabledTypes[appliesToType]?.deviceTypes[0]?.deviceType;
        debug && console.log('DeviceType', deviceType);
        return deviceType;
    }
    /**
     * This fetches the appropriate attributes that metric rules can use
     * It assumes that entity-state/SupportedEntities/Entity/StateType.calculated is correct, and
     * that the venture's calculateState function uses it accordingly, whilst propagating changes in to the ObjectHistory
     * This is the responsibility of the venture developer!
     */
    useEffect(() => {
        if (objectType) {
            // fetch a zombie device that the ruleset will process
            const deviceType = getDeviceType(objectType);
            const device = {
                ...getZombieInstanceFromPrimaryKey(`Device:ABC`),
                deviceType,
            };
            // fetch the CalculatedState class for the entity that matches with the device
            try {
                const entityStateBundle = objectType
                    ? matchEntityStates({
                        device,
                        entity: Assembler.getInstanceFromPrimaryKey(`${objectType}:xxx`),
                    })[0]
                    : undefined;
                debug && console.log('appliesToType???', objectType);
                debug && console.log('matchEntity', Assembler.getInstanceFromPrimaryKey(`Device:xxx`));
                debug && console.log('entity found', Assembler.getInstanceFromPrimaryKey(`${objectType}:xxx`));
                debug &&
                    console.log('matching', matchEntityStates({
                        device: Assembler.getInstanceFromPrimaryKey(`Device:xxx`),
                        entity: Assembler.getInstanceFromPrimaryKey(`${objectType}:xxx`),
                    }));
                debug && console.log('payload Def', entityStateBundle);
                debug && console.log('getDeviceEnabledTypes', getDeviceEnabledTypes());
                debug && console.log('getDeviceType', deviceType);
                const calcStateClass = entityStateBundle?.CalculatedState;
                const rules = calcStateClass?.getSystemRules?.();
                if (rules !== undefined)
                    setSystemRules(rules);
            }
            catch (e) {
                console.log(`SubscriptionLineItem: Err resolving payload`, e.message, e.stack);
            }
        }
        else {
            setSystemRules({});
        }
    }, [objectType]);
    useEffect(() => {
        setEdited(currentSubHash !== referenceSubHash);
    }, [currentSubHash]);
    useEffect(() => {
        if (subscription?.incidentSubtype)
            getObject(subscription.incidentSubtype, setIncidentSubtypeDefiningObject, setSystemRule);
    }, [subscription?.incidentSubtype]);
    useEffect(() => {
        const fetch = async (triggeringEntityOrGroup) => {
            setFetchingRulesets(true);
            const typeRulesets = async () => {
                const { data } = await ConsumerDao.getGraphQueryRequestV2({
                    contextObjects: [contextCustomer],
                    query: new BranchingQueryDefinition({
                        id: contextCustomer.sk,
                        path: [{ objectTypeId: 'Ruleset', adjacencyType: AdjacentType.CHILD, edgeTypeId: edgeTypeMatrix.Customer.Ruleset.customerHasRuleset.edgeTypeId }],
                    }),
                });
                const rulesets = data &&
                    data
                        .getNodesOfType('Ruleset')
                        .map((vob) => new Ruleset(vob))
                        .filter((r) => r.appliesToType === entityType && r.globallyApplyToObjectTypeId);
                return rulesets ?? [];
            };
            const groupRulesets = async (context) => {
                const { data: groupNem } = await ConsumerDao.getGraphQueryRequestV2({
                    contextObjects: [context],
                    query: getItemTypeToGroupNestedAscendingQueryDef({
                        sourceItemType: context.type,
                        groupDefiningObjectTypeId: context.type,
                    }),
                });
                if (groupNem) {
                    const groups = groupNem.getNodesOfType('Group');
                    const rulesetLists = await Promise.all(groups.map(async (group) => {
                        const { data } = await ConsumerDao.getGraphQueryRequestV2({
                            contextObjects: [group],
                            query: new BranchingQueryDefinition({
                                id: group.sk,
                                path: [
                                    { objectTypeId: 'Ruleset', adjacencyType: AdjacentType.PARENT, edgeTypeId: edgeTypeMatrix.Ruleset.Group.ruleset_has_group.edgeTypeId },
                                ],
                            }),
                        });
                        if (data) {
                            const rulesets = data.getNodesOfType('Ruleset').map((vob) => new Ruleset(vob));
                            return rulesets;
                        }
                        else {
                            return [];
                        }
                    }));
                    return rulesetLists.filter((rulesets) => rulesets.length).flat();
                }
                return [];
            };
            const rulesets = await Promise.all([typeRulesets(), triggeringEntityOrGroup ? groupRulesets(triggeringEntityOrGroup) : []]);
            const seen = new Set();
            const deduplicatedRulesets = rulesets.flat().filter((ruleset) => {
                const k = ruleset.id;
                return seen.has(k) ? false : seen.add(k);
            });
            setRulesets(deduplicatedRulesets.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0));
            setFetchingRulesets(false);
        };
        fetch(triggeringEntityOrGroup);
    }, [triggeringEntityOrGroup?.sk]);
    const handleSave = async () => {
        setSaving(true);
        debug && console.log('on save', objectType, triggeringEntityOrGroup, subscriptionState);
        if (typeof objectType === 'string' && (EntityGroupType === 'Type' || triggeringEntityOrGroup) && subscriptionState.status !== undefined) {
            const update = {
                deliveryPreferences: deliveryOptions,
                filter: {
                    severities,
                    statuses: [],
                    onActions: eventTypes,
                },
                incidentEntityOrGroupingSk: EntityGroupType === 'Type' ? objectType + ':*' : (triggeringEntityOrGroup?.sk ?? ''),
                incidentInitiatorSk: undefined,
                incidentProducingTypeId: objectType,
                incidentType: incidentType,
                incidentSubtype: systemRule ?? incidentSubtypeDefiningObject?.sk, // needs to be populated in case of business rule violation or system rule violation
                subscriptionState: subscriptionState,
            };
            const { sk, pk, id, type } = subscription;
            const notificationSubscription = new NotificationSubscription({ ...update, sk, pk, id, type });
            notificationSubscription.name = notificationSubscription.dsk;
            notificationSubscription.incidentEntityOrGroupingName = triggeringEntityOrGroup?.name ?? '';
            debug && console.log(`ON SAVE`, { incidentSubtypeDefiningObject, notificationSubscription });
            await ConsumerDao.save(notificationSubscription);
            await ConsumerDao.link(contextUser, notificationSubscription, edgeTypeMatrix.User.NotificationSubscription.User_has_NotificationSubscription.edgeTypeId);
            displayMessage(`Subscription for ${contextUser.name} saved!`, 'SnackBar', userFunctions);
            debug && console.log('SubscriptionLineItem about to force update on save');
            setSaving(false);
            updateInParent(notificationSubscription);
        }
        else {
            setSaving(false);
            debug && console.log(`data missing ( or wrong type) from`, { objectType, triggeringEntityOrGroup, subscriptionState });
        }
    };
    const handleDelete = async () => {
        setDeleting(true);
        const deleteRes = await deleteObject(subscription);
        if (!deleteRes.err) {
            debug && console.log('SubscriptionLineItem about to force update on delete');
            displayMessage(`Subscription for ${contextUser.name} deleted!`, 'SnackBar', userFunctions);
            updateInParent(undefined);
        }
        else {
            debug && console.log('Remove res err', deleteRes.err);
            debug && console.log('Input was ', JSON.stringify(subscription, null, 1));
            setDeleting(false);
        }
    };
    const updateEntityGroupType = (value) => {
        setTriggeringEntityOrGroup(null);
        setIncidentSubtypeDefiningObject(undefined);
        return setEntityGroupType(value);
    };
    const components = {
        title: (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', flexWrap: isSmallView ? 'nowrap' : 'wrap', gap: 1, children: [_jsxs(Typography, { variant: 'h5', children: ["Subscription for ", contextUser.name] }, 'title'), _jsx(LoadingButton, { startIcon: _jsx(SaveIcon, {}),
                    variant: 'outlined',
                    color: 'primary',
                    onClick: handleSave,
                    loading: saving,
                    loadingPosition: 'start',
                    disabled: !edited || deleting || invalid, children: "Save" }), _jsx(ConfirmLoadingButton, { startIcon: _jsx(ActionIcons.Delete, {}),
                    variant: 'outlined',
                    color: 'error',
                    onConfirm: handleDelete,
                    confirmMessage: 'Remove?',
                    loading: deleting,
                    disabled: saving || deleting, children: "Remove" }), invalid && (_jsx(Alert, { severity: "error", sx: { paddingBlock: 0 }, children: "Please fill all required fields before saving!" }))] }, 'title')),
        ruleType: (_jsx(Autocomplete, { freeSolo: false,
            id: 'selectRuleType',
            size: 'small',
            variant: 'outlined',
            value: ruleType ?? null,
            options: Object.values(RuleTypes).filter(isAdmin ? (x) => x : (rt) => !AdminOnlyRuleTypes.find((art) => art === rt)),
            getOptionLabel: toTitleCase,
            disabled: saving || deleting,
            onChange: (_, value) => {
                setRuleType(value ? value : undefined);
                setIncidentType(undefined);
            },
            renderInput: (params) => (_jsx(AutosizedTextField, { ...params,
                label: 'Rule Type',
                error: !deleting && ruleType === undefined })) }, 'ruleType')),
        alertIncidentType: ruleType && (_jsx(Autocomplete, { freeSolo: false,
            id: 'selectRuleType',
            size: 'small',
            variant: 'outlined',
            value: incidentType ?? null,
            options: Object.values(RuleToAlertIncidentMap[ruleType]),
            getOptionLabel: (option) => IncidentTypeNames[option],
            disabled: saving || deleting,
            onChange: (event, value) => {
                setIncidentType(value ?? undefined);
            },
            renderInput: (params) => (_jsx(AutosizedTextField, { ...params,
                label: 'Alert / Incident Type',
                error: !deleting && incidentType === undefined })) }, 'alertIncidentType')),
        objectType: incidentType && (_jsx(Autocomplete, { value: entityType ?? '', options: selectableEntityTypes, getOptionLabel: (option) => readableTypeName(option), onChange: (_, value) => {
                setTriggeringEntityOrGroup(null);
                setEntityType(value !== '' ? value : null);
                if (value === SystemClassName)
                    updateEntityGroupType('Entity');
                if (value === SystemClassName)
                    setTriggeringEntityOrGroup(new System({}));
            }, disabled: saving || deleting, disableClearable: true, renderInput: (params) => _jsx(AutosizedTextField, { ...params, label: "Entity type", size: "small", error: !deleting && !entityType }) }, 'objectType')),
        entityGroupType: objectType && (_jsxs(ButtonGroup, { children: [_jsx(Button, { variant: EntityGroupType === 'Entity' ? 'contained' : 'outlined', onClick: () => updateEntityGroupType('Entity'), disabled: objectType === SystemClassName, sx: { flexGrow: 1 }, children: entityType ? readableTypeName(entityType) : 'Entity' }), _jsx(Button, { variant: EntityGroupType === 'Group' ? 'contained' : 'outlined', onClick: () => updateEntityGroupType('Group'), disabled: objectType === SystemClassName, sx: { flexGrow: 1 }, children: entityType ? `${readableTypeName(entityType)} Group` : 'Group' }), (isAdmin || EntityGroupType === 'Type') && (_jsx(Button, { variant: EntityGroupType === 'Type' ? 'contained' : 'outlined', onClick: () => updateEntityGroupType('Type'), disabled: objectType === SystemClassName, sx: { flexGrow: 1 }, children: entityType ? `All ${pluralReadableTypeName(entityType)}` : 'All' }))] }, 'entityGroupType')),
        entity: objectType && (_jsxs(_Fragment, { children: [EntityGroupType === 'Entity' && objectType !== SystemClassName && objectType !== 'Group' && entityType && (_jsx(SingleObjectAutoQuery, { query: isAdmin
                        ? objectType !== NetworkApplicationServerClassName
                            ? getItemTypeToGroupNestedDescendingQueryDef({
                                groupDefiningObjectTypeId: 'Customer',
                                supportedType: entityType,
                                includeSupportedTypeInstances: true,
                            })
                            : new BranchingQueryDefinition({
                                id: SYSTEM.id,
                                path: [{ objectTypeId: NetworkApplicationServerClassName, adjacencyType: AdjacentType.CHILD }],
                            })
                        : getUserToEntitiesInVisibleGroupsQueryDef(entityType), contextObject: isAdmin ? (objectType !== NetworkApplicationServerClassName ? contextCustomer : SYSTEM) : contextUser, objectTypeId: entityType, required: !deleting, disabled: saving || deleting, selectedObject: triggeringEntityOrGroup, changeSelectedObject: (o) => setTriggeringEntityOrGroup(o) })), EntityGroupType === 'Group' && objectType === 'Group' && entityType && (_jsx(_Fragment, { children: _jsx(SingleObjectAutoQuery, { query: isAdmin
                            ? getItemTypeToGroupNestedDescendingQueryDef({
                                groupDefiningObjectTypeId: 'Customer',
                                supportedType: entityType,
                                includeSupportedTypeInstances: false,
                            })
                            : getUserToVisibleGroupsQueryDef(), contextObject: isAdmin ? contextCustomer : contextUser, objectTypeId: objectType, filter: (g) => g.supportedType === entityType, required: !deleting, disabled: saving || deleting, selectedObject: triggeringEntityOrGroup ?? undefined, changeSelectedObject: (o) => setTriggeringEntityOrGroup(o) }) }))] })),
        businessRuleChoice: incidentType === AlertIncidentType.BUSINESS_RULE_VIOLATION && (triggeringEntityOrGroup || EntityGroupType === 'Type') && (_jsx(Autocomplete, { disabled: saving || deleting, value: incidentSubtypeDefiningObject ?? null, loading: fetchingRulesets, options: !fetchingRulesets ? rulesets : [], getOptionLabel: (option) => option.name ?? option.sk, isOptionEqualToValue: (option, value) => option.sk === value.sk, forcePopupIcon: fetchingRulesets ? false : 'auto', onChange: (_, ruleset) => setIncidentSubtypeDefiningObject(ruleset ?? undefined), renderInput: (params) => {
                const InputProps = fetchingRulesets
                    ? {
                        ['InputProps']: {
                            endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { color: "inherit", size: 18 }) })),
                        },
                    }
                    : undefined;
                return (_jsx(AutosizedTextField, { ...params, ...InputProps, size: "small", label: `Rulesets (${!fetchingRulesets ? rulesets.length : '...'})`, error: !deleting && !incidentSubtypeDefiningObject }));
            } }, 'businessRuleChoice')),
        systemRuleChoice: incidentType === AlertIncidentType.SYSTEM_RULE_VIOLATION && (triggeringEntityOrGroup || EntityGroupType === 'Type') && (_jsx(Autocomplete, { disabled: saving || deleting, value: systemRule, options: Object.keys(systemRules), getOptionLabel: (o) => systemRules[o]?.name ?? '', onChange: (_, rule) => setSystemRule(rule), renderInput: (params) => (_jsx(AutosizedTextField, { ...params, size: "small", label: `Rules (${Object.keys(systemRules).length})`, error: !deleting && !systemRule })) }, 'systemRuleChoice')),
        severity: (_jsx(Autocomplete, { freeSolo: false,
            id: 'selectSeverity',
            size: 'small',
            variant: 'outlined',
            multiple: true,
            disabled: deleting,
            filterSelectedOptions: true,
            value: severities,
            options: Object.values(Severity),
            getOptionLabel: (option) => toTitleCase(camelCase_to_Sentence(option)),
            onChange: (_, v) => v && setSeverities(v),
            renderInput: (params) => (_jsx(AutosizedTextField, { ...params,
                label: 'Severity',
                disabled: saving || deleting })) }, 'severity')),
        eventType: (_jsx(Autocomplete, { freeSolo: false,
            id: 'select',
            size: 'small',
            variant: 'outlined',
            multiple: true,
            disabled: deleting,
            disableCloseOnSelect: true,
            filterSelectedOptions: true,
            value: eventTypes,
            options: Object.values(IOTV_Notifiable_EventType),
            getOptionLabel: (option) => toTitleCase(camelCase_to_Sentence(option)),
            onChange: (_, v) => v && setEventTypes(v),
            renderInput: (params) => (_jsx(AutosizedTextField, { ...params,
                label: 'Event',
                placeholder: 'Select an event',
                disabled: saving || deleting })) }, 'eventType')),
    };
    return (_jsxs(Stack, { gap: 1, children: [components.title, _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', gap: 1, children: [_jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Trigger" }), components.ruleType, components.alertIncidentType, components.objectType, components.entityGroupType, components.entity, components.businessRuleChoice, components.systemRuleChoice] }, 'trigger'), _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Subscription" }), _jsx(SubscriptionState, { subscriptionState, setSubscriptionState, disabled: saving || deleting })] }, 'subscription'), _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Filter" }), components.eventType, _jsxs(Alert, { severity: "info", children: ["Either list specific events to receive,", _jsx("br", {}), "or leave blank to receive all events"] })] })] }, 'subscription'), _jsx(Stack, { direction: isSmallView ? 'column' : 'row', children: _jsx(Button, { color: deliveryOptions.length ? 'inherit' : 'error', variant: "outlined", endIcon: expanded ? _jsx(ExpandLessIcon, {}) : _jsx(ExpandMoreIcon, {}), disabled: saving || deleting, onClick: () => setExpanded((e) => !e), children: _jsx(Typography, { children: "Deliveries" }) }) }, 'deliveries'), _jsx(Collapse, { in: expanded && !saving && !deleting, timeout: "auto", unmountOnExit: true, children: _jsx(DeliveryOptions, { contextObject: contextCustomer, deliveryOptions: deliveryOptions, setDeliveryOptions: setDeliveryOptions, additionalRecipients: {
                        [DeliveryType.SMS]: [...validPartnerRecipients],
                        [DeliveryType.EMAIL]: [...validPartnerRecipients, ...customersPeople],
                        [DeliveryType.MOBILE_PUSH]: [...validPartnerRecipients],
                    }, isAdmin: isAdmin, contextUser: contextUser }) })] }));
};
